/*
 * @author: wayou
 * @date: 2018-09-04 17:10:14
 * @description:
 * 替代原生图片标签，增加了
 * - 图片加载态
 * - 图片加载失败的回退展示
 * - 延迟加载 （只在支持 `IntersectionObserver` 的环境下才生效）
 */

import React, { Component } from 'react';
import imageApi from '@umu/umu-image-handle';
import { $document } from '@umu/umu-dom';
import { PreImg } from '../preImg/preImg';
import { isSlowNetwork, getNetworkStatus } from '../../services/networkInfo';
import { reportError } from '../../services/errorLogger';

import './emg.scss';

export enum EmgFit {
    'default',
    'contain',
    'cover',
}

import LOAD_ERR_IMG from './images/load_err.png';
import LOADING_IMG from './images/loading.png';

export { LOAD_ERR_IMG, LOADING_IMG };

interface IEmgProps {
    src: string;
    className?: string;
    alt?: string;
    title?: string;
    style?: React.CSSProperties;
    fit: EmgFit;

    /**
     * 加载态展示的图片
     */
    loadingImg?: string;

    /**
     * 加载失败展示的图片以标识失败状态，区别于 `fallbackImg`，它看起来是张错误图片
     */
    loadErrImg?: string;

    /**
     * 加载失败时的回退图片以替换正常需要展示的图片，区别于 `loadErrImg`，它看起来是张正常图片
     * 一般是整站的默认占位图片
     */
    fallbackImg?: string;

    /**
     * 是否启用 fallbackImg 重试功能
     * 当原始图片加载失败时，如果启用此功能且存在 fallbackImg，会尝试加载 fallbackImg
     * 如果 fallbackImg 也加载失败，则显示 loadErrImg
     */
    enableFallbackRetry?: boolean;

    /**
     * 是否启用延迟加载
     * 只在支持 [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
     * API 的环境下生效，不支持的浏览器则不会有延迟加载的功能
     */
    isLazyLoad?: boolean;

    /**
     * 主图加载失败时的重试次数，默认 2 次
     */
    retryCount?: number;

    /**
     * 主图重试的间隔（毫秒），默认 500ms
     */
    retryInterval?: number;

    onLoad?: (event: Event) => void;
    onError?: (event: Event) => void;
    onContextMenu?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

interface IEmgState {
    isLoading: boolean;
    isLoadSuccess: boolean;
    isFallbackLoading: boolean; // 新增：fallbackImg 是否正在加载
    isFallbackLoadSuccess: boolean; // 新增：fallbackImg 是否加载成功
}

const IMAGE_CLASS_NAMES = {
    IMAGE_CLASSNAME: 'emg',
    IMAGE_LOADING: 'emg-loading',
    IMAGE_LOAD_SUCCESS: 'emg-load-success',
    IMAGE_LOAD_ERR: 'emg-load-error',
};

const doc = $document.querySelector('.doc');

class Emg extends Component<IEmgProps, IEmgState> {
    public static defaultProps: Partial<IEmgProps> = {
        className: '',
        alt: '',
        loadingImg: LOADING_IMG,
        loadErrImg: LOAD_ERR_IMG,
        fallbackImg: '',
        enableFallbackRetry: false,
        isLazyLoad: true,
        fit: EmgFit.default,
        retryCount: 2,
        retryInterval: 500,
    };

    private imageElement?: HTMLImageElement;
    private retryAttempts: number = 0;
    private retryTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(props: IEmgProps) {
        super(props);

        this.state = {
            isLoading: true,
            isLoadSuccess: false,
            isFallbackLoading: false,
            isFallbackLoadSuccess: false,
        };
    }

    public componentDidMount() {
        const { src, isLazyLoad } = this.props;

        if (false && isLazyLoad && 'IntersectionObserver' in window) {
            this.initalLazyLoad();
        } else {
            this.loadImg(src);
        }
    }

    public componentWillUnmount() {
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }

    public render() {
        const { alt, title, style, className, fit, onContextMenu } = this.props;
        const { isLoading, isLoadSuccess } = this.state;
        const imgSrc = this.getImgUrl();

        const isDefaultFit = isLoading || fit === EmgFit.default;
        const classNames = this.getClassName(isDefaultFit ? className : '', isLoading, isLoadSuccess);
        const defaultFit = (
            <PreImg>
                <img
                    ref={(ref) => {
                        this.imageElement = ref as HTMLImageElement;
                    }}
                    className={classNames}
                    src={imgSrc}
                    alt={alt}
                    title={title}
                    style={style}
                />
            </PreImg>
        );

        if (!isDefaultFit) {
            let height: string | number = 'auto';
            if (doc && this.props.fit === EmgFit.cover) {
                height = Math.floor(doc.clientWidth / 3 - 7);
            }
            const containStyle = {
                backgroundImage: `url('${imageApi.process(imgSrc)}')`,
            };
            return (
                <div className={`emg-fit-${fit}`} style={{ ...containStyle, ...style }} onContextMenu={onContextMenu}>
                    <PreImg>
                        <img src={imgSrc} className="emg" style={{ height }} />
                    </PreImg>
                </div>
            );
        }
        return defaultFit;
    }

    private getImgUrl(): string {
        const { src, loadingImg, loadErrImg, fallbackImg, enableFallbackRetry } = this.props;
        const { isLoading, isLoadSuccess, isFallbackLoading, isFallbackLoadSuccess } = this.state;

        if (isLoading || isFallbackLoading) {
            return loadingImg!;
        } else if (isLoadSuccess) {
            return src;
        } else if (enableFallbackRetry && isFallbackLoadSuccess && fallbackImg) {
            return fallbackImg;
        } else if (!enableFallbackRetry && fallbackImg) {
            // 兼容原有逻辑：不启用重试时直接显示 fallbackImg
            return fallbackImg;
        } else {
            return loadErrImg!;
        }
    }

    private getClassName(className: string = '', isLoading: boolean, isLoadSuccess: boolean): string {
        const { isFallbackLoading, isFallbackLoadSuccess } = this.state;

        return [
            IMAGE_CLASS_NAMES.IMAGE_CLASSNAME,
            !!className ? className : '',
            isLoading || isFallbackLoading
                ? IMAGE_CLASS_NAMES.IMAGE_LOADING
                : isLoadSuccess || isFallbackLoadSuccess
                ? IMAGE_CLASS_NAMES.IMAGE_LOAD_SUCCESS
                : IMAGE_CLASS_NAMES.IMAGE_LOAD_ERR,
        ].join(' ');
    }

    private onLoad = (event: Event): void => {
        const { onLoad } = this.props;
        const { isFallbackLoading } = this.state;

        if (isFallbackLoading) {
            // fallbackImg 加载成功
            this.setState({
                isFallbackLoading: false,
                isFallbackLoadSuccess: true,
            });
        } else {
            // 原始图片加载成功
            this.setState({
                isLoading: false,
                isLoadSuccess: true,
            });
            this.retryAttempts = 0;
        }

        if (onLoad) {
            onLoad(event);
        }
    };

    private onError = (event: Event | string): void => {
        if (typeof event === 'string') {
            return;
        }

        const { onError, fallbackImg, enableFallbackRetry, retryCount, retryInterval } = this.props;
        const { isLoading, isFallbackLoading } = this.state;

        if (!isFallbackLoading) {
            // 主图加载失败：先进行重试
            const maxRetries = Math.max(0, typeof retryCount === 'number' ? retryCount : 2);
            const retryDelay = Math.max(0, typeof retryInterval === 'number' ? retryInterval : 500);

            if (this.retryAttempts < maxRetries) {
                this.retryAttempts += 1;
                if (this.retryTimer) {
                    clearTimeout(this.retryTimer);
                }
                this.retryTimer = setTimeout(() => {
                    this.loadImg(this.props.src);
                }, retryDelay);
            } else if (isLoading && fallbackImg && enableFallbackRetry) {
                // 重试已耗尽，开始加载 fallback
                this.setState({
                    isLoading: false,
                    isLoadSuccess: false,
                    isFallbackLoading: true,
                    isFallbackLoadSuccess: false,
                });

                reportError({
                    error: event,
                    componentName: 'Emg',
                    additionalInfo: {
                        url: this.props.src,
                        retryAttempts: this.retryAttempts,
                        networkStatus: getNetworkStatus(),
                        isSlowNetwork: isSlowNetwork(),
                        stage: 'main_image_retries_exhausted_fallback_loading',
                    },
                });

                this.loadFallbackImg(fallbackImg);
            } else {
                // 无 fallback 或未启用 fallback，直接失败
                this.setState({
                    isLoading: false,
                    isLoadSuccess: false,
                    isFallbackLoading: false,
                    isFallbackLoadSuccess: false,
                });

                reportError({
                    error: event,
                    componentName: 'Emg',
                    additionalInfo: {
                        url: this.props.src,
                        retryAttempts: this.retryAttempts,
                        networkStatus: getNetworkStatus(),
                        isSlowNetwork: isSlowNetwork(),
                        stage: 'main_image_retries_exhausted_no_fallback',
                    },
                });
            }
        } else {
            // 未启用 fallback 重试功能或 fallbackImg 也加载失败，显示 loadErrImg
            this.setState({
                isLoading: false,
                isLoadSuccess: false,
                isFallbackLoading: false,
                isFallbackLoadSuccess: false,
            });
        }

        if (onError) {
            onError(event);
        }
    };

    private initalLazyLoad() {
        const imageElement = this.imageElement;
        if (!imageElement) {
            return;
        }

        if ('IntersectionObserver' in window) {
            const lazyImageObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const lazyImage = entry.target as HTMLImageElement;
                        this.loadImg(this.props.src);
                        lazyImageObserver.unobserve(lazyImage);
                    }
                });
            });

            lazyImageObserver.observe(imageElement);
        }
    }

    private loadImg(src: string) {
        const loader = new Image();
        loader.onload = this.onLoad;
        loader.onerror = this.onError;
        loader.src = src;
    }

    private loadFallbackImg(src: string) {
        const loader = new Image();
        loader.onload = this.onLoad;
        loader.onerror = this.onError;
        loader.src = src;
    }
}

export default Emg;
